<template>
  <EventsList
    :title="title"
    :events-list="eventsStore.list.hits"
    :all-loaded="eventsStore.allEventsLoaded"
    :loading="eventsStore.list.processing"
    :loaded="
      listType === EventsListTypeMap.Me && !authStore.auth
        ? true
        : eventsStore.list.loaded
    "
    @next="eventsStore.getNext()"
  >
    <template #buttons>
      <div class="flex items-center gap-2 md:order-last min-h-12">
        <BaseButton
          v-show="eventsStore.filtersCount > 0"
          look="none"
          class="bg-ocean !px-3"
          @click="onClear"
        >
          <div class="flex gap-1 items-center font-normal text-sm">
            <div>
              {{ $t('events.filters_applied', eventsStore.filtersCount) }}
            </div>
            <BaseIcon name="outline_x" size="xs" />
          </div>
        </BaseButton>
        <BaseButton
          look="none"
          class="w-9 h-9 !p-1"
          :class="isShowFilters && 'bg-ocean'"
          rounded="full"
          @click="isShowFilters = !isShowFilters"
        >
          <BaseIcon
            name="outline_adjustments_horizontal"
            class="text-black"
            size="md"
          />
        </BaseButton>
      </div>
    </template>
    <template #filters>
      <div
        v-show="isShowFilters"
        class="flex gap-2 min-w-full md:min-w-min flex-1 md:justify-end min-h-12 items-center"
        :class="
          listType === EventsListTypeMap.Me && !authStore.auth && 'hidden'
        "
      >
        <MenuSelect
          v-model="eventsStore.list.params[FilterParams.EventSubtype]"
          :title="$t('events.event_type')"
          name="type"
          :options="availableSubtypeOptions"
          @update:model-value="onUpdateSubtype"
        />
        <MenuSelect
          v-model="eventsStore.list.params[FilterParams.EventRegions]"
          :title="$t('events.region')"
          name="regions"
          multiple
          menu-direction="row"
          :options="availableRegionOptions"
          @update:model-value="onUpdateRegions"
        />
      </div>
    </template>
    <template #empty>
      <slot name="empty" />
    </template>
  </EventsList>
</template>

<script setup lang="ts">
import { useEvents } from '~/stores/events'
import { useAuth } from '~/stores/auth'
import {
  EventType,
  EventRegion,
  EventSubtype,
  type EventsListType,
  EventsListTypeMap,
  EventSubtypeOptionsSorted,
  EventRegionOptions,
} from '~/models/events'
import { FilterParams } from '~/stores/params-helper'

interface Props {
  title: string
  eventType: EventType
  listType: EventsListType
}

const props = defineProps<Props>()

const authStore = useAuth()
const eventsStore = useEvents()
const route = useRoute()
const router = useRouter()
const isShowFilters = ref(false)

const subtypeQuery = route.query[FilterParams.EventSubtype]
  ? Number(route.query[FilterParams.EventSubtype])
  : undefined

const regionsQuery = route.query[FilterParams.EventRegions]
  ? (route.query[FilterParams.EventRegions] as string)
      .split(',')
      .map((item) => Number(item))
  : []

eventsStore.resetEventsListWithType(
  props.listType,
  EventType.Default,
  subtypeQuery,
  regionsQuery
)

const availableRegionOptions = computed(() => {
  return EventRegionOptions.filter((option) =>
    eventsStore.filters?.region?.values.some(
      (filter) => String(filter.value) === String(option.value)
    )
  )
})

const availableSubtypeOptions = computed(() => {
  return EventSubtypeOptionsSorted.filter((option) =>
    eventsStore.filters?.event_type?.values.some(
      (filter) => String(filter.value) === String(option.value)
    )
  )
})

const onUpdateRegions = async (regions: EventRegion[]) => {
  eventsStore.list.params[FilterParams.EventRegions] = regions

  let queryParams = { ...router.currentRoute.value.query }
  if (regions.length) {
    queryParams = {
      ...queryParams,
      [FilterParams.EventRegions]: regions.join(','),
    }
  } else {
    delete queryParams[FilterParams.EventRegions]
  }

  router.push({
    query: queryParams,
  })

  await eventsStore.fetchEventsFilters()
  eventsStore.reset()
  await eventsStore.getEvents(1)
}

const onUpdateSubtype = async (subtype: EventSubtype) => {
  eventsStore.list.params[FilterParams.EventSubtype] = subtype

  let queryParams = { ...router.currentRoute.value.query }
  if (typeof subtype === 'number') {
    queryParams = {
      ...queryParams,
      [FilterParams.EventSubtype]: subtype.toString(),
    }
  } else {
    delete queryParams[FilterParams.EventSubtype]
  }

  router.push({
    query: queryParams,
  })

  await eventsStore.fetchEventsFilters()
  eventsStore.reset()
  await eventsStore.getEvents(1)
}

const clearFilters = () => {
  eventsStore.list.params[FilterParams.EventSubtype] = undefined
  eventsStore.list.params[FilterParams.EventRegions] = []

  router.push({
    query: {},
  })
}

const onClear = async () => {
  clearFilters()
  eventsStore.reset()
  await eventsStore.getEvents(1)
  await eventsStore.fetchEventsFilters()
}

onServerPrefetch(async () => {
  if (props.listType === EventsListTypeMap.Me && !authStore.auth) {
    return
  }
  await eventsStore.getEvents()
  await eventsStore.fetchEventsFilters()
})

onBeforeMount(async () => {
  if (props.listType === EventsListTypeMap.Me && !authStore.auth) {
    return
  }
  await eventsStore.getEvents()
  if (!useNuxtApp().isHydrating) {
    await eventsStore.fetchEventsFilters()
  }
})

watch(
  () => !!authStore.auth,
  (isAuth) => {
    if (props.listType === EventsListTypeMap.Me && !isAuth) {
      clearFilters()
      eventsStore.reset()
      return
    }

    eventsStore.resetEventsListWithType(
      props.listType,
      props.eventType,
      eventsStore.list.params[FilterParams.EventSubtype] as EventSubtype,
      eventsStore.list.params[FilterParams.EventRegions] as EventRegion[]
    )
    eventsStore.getEvents()
  }
)
</script>
